import React, { useState } from 'react';
import { useStripe, useElements, PaymentElement, LinkAuthenticationElement, AddressElement } from '@stripe/react-stripe-js';
import { Lock, AlertCircle } from 'lucide-react';
import { api } from '@/lib/api';

export default function CheckoutForm({ summary, paymentIntentId, onProcessing, onSuccess, onError, initialEmail = '' }) {
  const stripe = useStripe();
  const elements = useElements();
  const [email, setEmail] = useState(initialEmail);
  const [name, setName]   = useState('');
  const [processing, setProcessing] = useState(false);
  const [errMsg, setErrMsg] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (!name.trim()) {
      setErrMsg('Please enter your full name.');
      onError?.('Missing name');
      return;
    }
    setErrMsg(null); setProcessing(true); onProcessing?.();

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setErrMsg(submitError.message || 'Please review the details above.');
      setProcessing(false); onError?.(submitError.message);
      return;
    }

    // Attach buyer identity to the EXISTING PaymentIntent (email + name +
    // metadata) instead of creating a duplicate PI. This calls PaymentIntent.modify
    // server-side; it can only update non-price-critical fields.
    if (paymentIntentId) {
      try {
        await api.post(`/payment-intent/${paymentIntentId}/buyer`, {
          customer_email: email.trim().toLowerCase() || undefined,
          customer_name:  name.trim() || undefined,
        });
      } catch { /* non-blocking; buyer identity also passed via billing_details below */ }
    }

    const returnUrl = `${window.location.origin}/checkout/${summary.product_id}?pi=${paymentIntentId}`;
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: returnUrl,
        receipt_email: email.trim().toLowerCase() || undefined,
        payment_method_data: {
          billing_details: { name: name.trim() || undefined, email: email.trim().toLowerCase() || undefined },
        },
      },
      redirect: 'if_required',
    });

    if (error) {
      setErrMsg(error.message || 'Payment failed.');
      setProcessing(false); onError?.(error.message);
      return;
    }
    if (paymentIntent?.status === 'succeeded') {
      onSuccess?.(paymentIntent);
    } else if (paymentIntent?.status === 'processing') {
      onSuccess?.(paymentIntent); // will finalise via webhook
    } else {
      setErrMsg(`Payment status: ${paymentIntent?.status || 'unknown'}`);
      onError?.(paymentIntent?.status);
    }
    setProcessing(false);
  };

  const money = (n) => {
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: (summary?.currency || 'USD').toUpperCase() }).format(Number(n || 0)); }
    catch { return `${Number(n || 0).toFixed(2)}`; }
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4" data-testid="checkout-form">
      <div className="pg-admin-card rounded-[var(--pg-radius-md)] p-5 space-y-3">
        <p className="pg-label text-[10px]">CONTACT</p>
        <LinkAuthenticationElement onChange={(e) => setEmail(e.value.email || '')} options={{ defaultValues: { email: initialEmail } }} />
        <div>
          <label className="text-[10px] font-mono tracking-widest uppercase text-[var(--pg-ink-faint)]">Full name</label>
          <input
            value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            data-testid="checkout-name"
            className="mt-1 w-full pg-input rounded-[var(--pg-radius-sm)] px-3 py-2.5 text-sm text-[var(--pg-ink)] focus:outline-none focus:ring-2 focus:ring-[rgba(226,199,122,0.5)]"
          />
        </div>
      </div>
      <div className="pg-admin-card rounded-[var(--pg-radius-md)] p-5 space-y-3">
        <p className="pg-label text-[10px]">PAYMENT</p>
        <PaymentElement options={{ layout: 'accordion' }} />
        <p className="text-[10px] font-mono text-[var(--pg-ink-faint)] flex items-center gap-1">
          <Lock size={10} /> Secure payments powered by Stripe · Apple Pay · Google Pay · Link
        </p>
      </div>

      <div className="pg-admin-card rounded-[var(--pg-radius-md)] p-5 space-y-3">
        <p className="pg-label text-[10px]">BILLING ADDRESS</p>
        <AddressElement options={{ mode: 'billing', fields: { phone: 'auto' } }} />
      </div>

      {errMsg && (
        <div className="pg-input rounded-[var(--pg-radius-sm)] px-3 py-2.5 flex items-center gap-2 text-xs text-[var(--pg-neon-pink)]" data-testid="checkout-error">
          <AlertCircle size={14} /> {errMsg}
        </div>
      )}

      <button type="submit" disabled={!stripe || processing} data-testid="checkout-pay-button"
              className="w-full pg-btn-primary rounded-[var(--pg-radius-sm)] px-4 py-3 text-sm font-mono tracking-widest uppercase disabled:opacity-60">
        {processing ? 'Processing…' : `Pay ${money(summary?.total)}`}
      </button>

      <p className="text-center text-[10px] font-mono text-[var(--pg-ink-faint)]">By continuing, you agree to Planet G’s terms and privacy policy.</p>
    </form>
  );
}
